import React, { useContext, useEffect, useRef, useState } from 'react';

import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { PVMUrls } from 'config/urls';
import { monthData } from 'utils/Calender';

const errorMessage: string = 'Something went wrong!';

interface outputType {
    data: any;
    loading: boolean;
    error: any;
}

interface inputType {
    setSelectedStartMonth: Function;
    setSelectedEndMonth: Function;
    plans: string[];
    queryConfig?: {
        enabled?: boolean;
        cacheTime?: number;
    };
}

// common api handler
function useFetchYearDropdownValues({
    setSelectedStartMonth,
    setSelectedEndMonth,
    plans,
    queryConfig = {},
}: inputType): outputType {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery<any, any>({
        queryKey: ['pvm-year-dropdown-values'],
        queryFn: async () => {
            //initializing states
            setData(null);
            setError(null);
            setLoading(true);
            try {
                const data = await axios({
                    method: 'post',
                    url: PVMUrls.YearDropdownUrl,
                    data: { feature_name: 'pvm', comparison_plans: plans },
                });
                return data;
            } catch (error) {
                console.log('err ', error);
                onFetchError(error);
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    //error handling
    if (!apiLoading && apiError && !error) {
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const parsedData = payload?.map((i: any) => {
                return {
                    value: `${i.month}-${i?.year}`,
                    label: `${monthData.find((m: any) => m.key === i.month)?.label}  ${i?.year}`,
                };
            });
            setSelectedStartMonth(parsedData[0]);
            setSelectedEndMonth(parsedData[0]);

            setData(parsedData);
        } catch (error) {
            onFetchError(error);
            console.error(errorMessage, error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
    };
    return outputData;
}

export { useFetchYearDropdownValues };
